import { useModuleBEM } from "@b2bportal/core-themes";
import type {
  InputErrorType,
  PlatformComponentProps,
} from "@b2bportal/core-types";
import { useDateRangePicker } from "@tenant/cars";
import {
  Calendar,
  DatePickerButton,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  PickerType,
} from "@tenant/ui";
import defaultStyles from "./CarDateRangePicker.module.scss";
import { useCarsStyles } from "~core-cars/themes";
import { CarsRawEventName, useEmitCoreCarsRawEvent } from "~core-cars/tracking";
import { CoreCarsComponent } from "~core-cars/types";

export type CarDateRangePickerComponentProps = {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChangeHandler: (date: Date | undefined) => void;
  onEndDateChangeHandler: (date: Date | undefined) => void;
  onDoneClick?: () => void;
  onClose?: () => void;
  dialogTitle?: string;
  header?: React.ReactNode;
  error?: InputErrorType;
  triggerButton?: JSX.Element;
  ariaLabel?: string;
} & PlatformComponentProps;

export const CarDateRangePicker = (props: CarDateRangePickerComponentProps) => {
  const COMPONENT_KEY = CoreCarsComponent.CarDateRangePicker;
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { triggerButton, header, ariaLabel } = props;
  const { context, handlers, text } = useDateRangePicker(props);

  const emitCalendarViewedEvent = useEmitCoreCarsRawEvent(
    CarsRawEventName.CarViewedCalendar
  );

  return (
    <Dialog onOpen={emitCalendarViewedEvent} onClose={handlers.onClose}>
      <DialogTrigger asChild>
        {triggerButton ?? (
          <DatePickerButton
            startDate={context.stringStartDate}
            endDate={context.stringEndDate}
            startLabel={text.startDateLabel}
            endLabel={text.endDateLabel}
            hideSeparator={false}
            error={context.error}
            ariaLabel={ariaLabel}
          />
        )}
      </DialogTrigger>
      <DialogContent
        title={text.dialogTitle}
        header={header}
        className={cn("dialog-content")}
        fullScreen={context.dialogFullscreen}
        bottomFixedContent={
          <DialogClose asChild>
            <button
              className={cn("apply-button")}
              onClick={() => {
                handlers.onDoneClick?.();
              }}
            >
              {text.dialogDone}
            </button>
          </DialogClose>
        }
      >
        <Calendar
          className={cn("calendar")}
          viewType={context.calendarViewType}
          setStartDate={handlers.onStartDateChange}
          setEndDate={handlers.onEndDateChange}
          startDate={context.startDate}
          endDate={context.endDate}
          startDateLabel={text.startDateLabel}
          endDateLabel={text.endDateLabel}
          pickerType={PickerType.RANGE}
          priceTagsElement={<></>}
          months={[]}
          currency={""}
          priceTags={[]}
        />
      </DialogContent>
    </Dialog>
  );
};
